import { prepareExpander } from "../expander/Expander";
import { OverflowBox, prepareOverflowBoxContainers } from "../overflowBox/OverflowBox";
import { FacetGroupSelect } from "./FacetGroupSelect";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import type { DresonRuleResolvedEvent, FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import { element } from "../util/Utils";
import { FilterRelevancy } from "./FilterRelevancy";
import { FilterlistExpander } from "../filterlistExpander/FilterlistExpander";
import { Filter } from "./Filter";
import { events } from "../events";
import { eventQBus } from "../types/EventQBus";
import { prepareCategoryPathFacet } from "./SvelteFacet";
import {
  initializeConfigurableFacet,
  initializeFilterFormComponents,
  initializeFilterListComponent,
  initializeFilterTitlesComponent,
  setOpenFilterIds,
} from "./SvelteComponentInitialization";
import { OcFilterAccordion } from "./OcFilterAccordion";
import { isActive } from "../toggle/Toggle";

const FILTER_SECTION_ID = "find_filterSection";
const ERROR_MESSAGE_SELECTOR = ".heureka_filterSection--errorMessage";

export class FilterSection {
  static readonly factory = HeurekaElementFactory.byId(FILTER_SECTION_ID, FilterSection);

  /*               */
  constructor(readonly filterSection: HTMLElement) {}

  /*                  */
  static template(rootElement?: ParentNode | null) {
    return FilterSection.factory.pick(undefined, rootElement);
  }

  /*               */
  static register() {
    /*      */
    eventQBus.on("ftfind.dresonRule.resolve", FilterSection.lockAll);
    eventQBus.on("ftfind.dresonRule.resolved", FilterSection.unlockAllImmediately);
    eventQBus.on("ftfind.dresonRule.resolveAborted", FilterSection.unlockAll);

    /*        */
    events.onFilterSectionLoaded(FilterSection.unlockAll);
    events.onFilterSectionLoadAborted(FilterSection.unlockAll);

    /*     */
    events.onFilterSectionLoadAborted(FilterSection.showErrorMessage);
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    FilterSection.factory.forEach((filterSection) => filterSection.init(), rootElement);
  }

  static prepare(fragment: DocumentFragment, initialLoad = false) {
    prepareExpander(fragment);
    OverflowBox.prepareAll(fragment);
    prepareOverflowBoxContainers(fragment);
    FilterRelevancy.prepareAll(fragment, initialLoad);
    if (!initialLoad) {
      /*                                                                        */
      prepareCategoryPathFacet(fragment);
    }
    /*                                                                */
    setOpenFilterIds([]);
    if (isActive("HEUREKA_2167_MIGRATE_FILTERS_TO_SVELTE")) {
      initializeFilterListComponent(fragment);
      initializeFilterFormComponents(fragment);
      initializeFilterTitlesComponent(fragment);
    }
    initializeConfigurableFacet(fragment);
    OcFilterAccordion.prepareAll(fragment);
    FacetGroupSelect.prepareAll(fragment);
    Filter.prepareAll(fragment, initialLoad);
    FilterlistExpander.prepareAll(fragment);

    const allFilterIds = OcFilterAccordion.factory.all(fragment).map((a) => a.filterId);
    /*                           */
    setOpenFilterIds(
      OcFilterAccordion.factory
        .all(fragment)
        .filter((accordion) => accordion.open)
        .map((accordion) => accordion.filterId),
    );
    /*                                     */
    setTimeout(() => {
      setOpenFilterIds(allFilterIds);
    }, 0);
  }

  static showErrorMessage() {
    FilterSection.factory.forEach((filterSection) => filterSection.showError());
  }

  static lockAll() {
    FilterSection.factory.forEach((filterSection) => filterSection.lock());
  }

  static unlockAll() {
    FilterSection.factory.forEach((filterSection) => filterSection.unlock());
  }

  static unlockAllImmediately(event: DresonRuleResolvedEvent) {
    if (event.count == 0 || event.rule === FilterSection.template().currentDresonRule) {
      /*                 */
      FilterSection.unlockAll();
    }
  }

  public get currentDresonRule() {
    return this.filterSection.getAttribute("data-rule");
  }

  protected init() {
    return this;
  }

  public lock() {
    this.filterSection.classList.add("filterWrapper--loading");
    return this;
  }

  public unlock() {
    this.filterSection.classList.remove("filterWrapper--loading");
    return this;
  }

  public get errorMessage() {
    return element(ERROR_MESSAGE_SELECTOR, this.filterSection);
  }

  public showError() {
    const { errorMessage } = this;
    if (errorMessage) {
      errorMessage.hidden = false;
      errorMessage.style.display = "";
    }
  }
}
